import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, Quote } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export const TestimonialsSection = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "What Our Clients Say",
      subtitle: "Real results from real businesses",
      testimonials: [
        {
          name: "Carlos Rodriguez",
          role: "CEO",
          company: "TechSolutions LATAM",
          industry: "SaaS Technology",
          content: "PriceZing helped us redesign our pricing strategy completely. We saw a 18% increase in revenue within just 4 months. Their data-driven approach was exactly what we needed.",
          rating: 5,
          result: "+18% Revenue Growth"
        },
        {
          name: "Maria Gonzalez",
          role: "VP of Sales",
          company: "EcoFashion",
          industry: "Retail Fashion", 
          content: "The Smart PriceZing platform transformed how we monitor competitor prices. We can now react to market changes in real-time instead of weeks. Absolutely game-changing.",
          rating: 5,
          result: "Real-time Price Optimization"
        },
        {
          name: "James Mitchell",
          role: "Founder",
          company: "GlobalTrade Inc",
          industry: "International Commerce",
          content: "Their international market research was instrumental in our successful expansion to 5 Latin American countries. The certified reports met all regulatory requirements perfectly.",
          rating: 5,
          result: "5 Market Expansion"
        }
      ]
    },
    es: {
      title: "Lo Que Dicen Nuestros Clientes",
      subtitle: "Resultados reales de negocios reales",
      testimonials: [
        {
          name: "Carlos Rodriguez",
          role: "CEO",
          company: "TechSolutions LATAM",
          industry: "Tecnología SaaS",
          content: "PriceZing nos ayudó a rediseñar completamente nuestra estrategia de precios. Vimos un aumento del 18% en ingresos en solo 4 meses. Su enfoque basado en datos era exactamente lo que necesitábamos.",
          rating: 5,
          result: "+18% Crecimiento de Ingresos"
        },
        {
          name: "María González",
          role: "VP de Ventas", 
          company: "EcoFashion",
          industry: "Moda Retail",
          content: "La plataforma Smart PriceZing transformó cómo monitoreamos precios de competidores. Ahora podemos reaccionar a cambios del mercado en tiempo real en lugar de semanas. Absolutamente revolucionario.",
          rating: 5,
          result: "Optimización de Precios en Tiempo Real"
        },
        {
          name: "James Mitchell",
          role: "Fundador",
          company: "GlobalTrade Inc",
          industry: "Comercio Internacional",
          content: "Su investigación de mercado internacional fue instrumental en nuestra expansión exitosa a 5 países latinoamericanos. Los reportes certificados cumplieron todos los requisitos regulatorios perfectamente.",
          rating: 5,
          result: "Expansión a 5 Mercados"
        }
      ]
    }
  };

  const current = content[language];

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{current.title}</h2>
          <p className="text-xl text-muted-foreground">{current.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {current.testimonials.map((testimonial, index) => (
            <Card key={index} className="shadow-soft hover:shadow-medium transition-all relative">
              <CardContent className="p-6">
                {/* Quote Icon */}
                <Quote className="w-8 h-8 text-primary/20 mb-4" />
                
                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {renderStars(testimonial.rating)}
                </div>
                
                {/* Testimonial Content */}
                <blockquote className="text-muted-foreground mb-6 italic">
                  "{testimonial.content}"
                </blockquote>
                
                {/* Result Badge */}
                <Badge variant="secondary" className="mb-4 bg-success/10 text-success hover:bg-success/20">
                  {testimonial.result}
                </Badge>
                
                {/* Author Info */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{testimonial.name}</div>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.role}
                      </p>
                      <p className="text-sm font-medium text-primary">
                        {testimonial.company}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {testimonial.industry}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-3xl font-bold text-primary mb-2">300+</div>
            <div className="text-sm text-muted-foreground">
              {language === 'en' ? 'Projects Completed' : 'Proyectos Completados'}
            </div>
          </div>
          <div>
            <div className="text-3xl font-bold text-primary mb-2">15+</div>
            <div className="text-sm text-muted-foreground">
              {language === 'en' ? 'Countries Served' : 'Países Atendidos'}
            </div>
          </div>
          <div>
            <div className="text-3xl font-bold text-primary mb-2">95%</div>
            <div className="text-sm text-muted-foreground">
              {language === 'en' ? 'Client Satisfaction' : 'Satisfacción del Cliente'}
            </div>
          </div>
          <div>
            <div className="text-3xl font-bold text-primary mb-2">$50M+</div>
            <div className="text-sm text-muted-foreground">
              {language === 'en' ? 'Revenue Generated' : 'Ingresos Generados'}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};